/**
 * useCalibrationModeDetection Hook
 * 
 * Detects if current filter state matches calibration mode.
 * Checks if the user has selected the 4 DataBrain calibration metrics.
 */

import { useMemo } from 'react';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';
import { DATABRAIN_CALIBRATION_METRICS } from '../const';

interface UseCalibrationModeDetectionParams {
  filterValue: any;
}

interface UseCalibrationModeDetectionResult {
  isInCalibrationMode: boolean;
}

/**
 * Check if metrics array matches DataBrain calibration metrics
 */
function isDatabrainCalibrationMetricSelection(metrics: string[] = []): boolean {
  if (!Array.isArray(metrics)) return false;
  if (metrics.length !== DATABRAIN_CALIBRATION_METRICS.length) return false;
  
  const metricsSet = new Set(metrics);
  return DATABRAIN_CALIBRATION_METRICS.every((metric) => metricsSet.has(metric));
}

/**
 * Hook to detect if current filter state is in calibration mode
 * 
 * @param filterValue - Current filter state
 * @returns Whether the current state matches calibration mode
 * 
 * @example
 * ```typescript
 * const { isInCalibrationMode } = useCalibrationModeDetection({ filterValue });
 * if (isInCalibrationMode) {
 *   // Show calibration-specific UI
 * }
 * ```
 */
export function useCalibrationModeDetection(
  params: UseCalibrationModeDetectionParams
): UseCalibrationModeDetectionResult {
  const { filterValue } = params;

  const isInCalibrationMode = useMemo(() => {
    const metricSelection = filterValue?.[filterKeyMap.METRIC_SELECT_FOR_KPI_TREND]?.value;
    const metrics: string[] = metricSelection?.metrics || [];
    const selectedMetricsValue = metricSelection?.selectedMetricsValue;
    const shotSelectValue = metricSelection?.shotSelectValue;

    const isDatabrainSelection =
      isDatabrainCalibrationMetricSelection(metrics) &&
      selectedMetricsValue === 'dataSource' &&
      shotSelectValue === 'databrain';

    return isDatabrainSelection;
  }, [filterValue]);

  return { isInCalibrationMode };
}
